import { createClient } from "@supabase/supabase-js";
import type { SupabaseClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabaseRealtimeAnonKey =
  import.meta.env.VITE_SUPABASE_REALTIME_ANON_KEY ?? supabaseAnonKey;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Missing Supabase environment variables");
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

function isLocalSupabaseUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.hostname === "127.0.0.1" || parsed.hostname === "localhost";
  } catch {
    return false;
  }
}

if (
  import.meta.env.DEV &&
  isLocalSupabaseUrl(supabaseUrl) &&
  supabaseAnonKey.startsWith("sb_publishable_") &&
  !import.meta.env.VITE_SUPABASE_REALTIME_ANON_KEY
) {
  console.warn(
    "Local Supabase Realtime needs a JWT anon key. Set VITE_SUPABASE_REALTIME_ANON_KEY to avoid CHANNEL_ERROR subscriptions.",
  );
}

export const supabaseRealtime =
  supabaseRealtimeAnonKey === supabaseAnonKey
    ? supabase
    : createClient(supabaseUrl, supabaseRealtimeAnonKey, {
        auth: {
          autoRefreshToken: false,
          detectSessionInUrl: false,
          persistSession: false,
        },
      });

/** Storage adapter interface compatible with Supabase auth (getItem/setItem/removeItem). */
export type SupabaseStorageAdapter = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
};

/**
 * Create a Supabase client that uses the given storage for auth session.
 * Used in the session-bridge iframe when Storage Access API grants unpartitioned localStorage.
 */
export function createSupabaseClientWithStorage(storage: Storage): SupabaseClient {
  const adapter: SupabaseStorageAdapter = {
    getItem: (key) => Promise.resolve(storage.getItem(key)),
    setItem: (key, value) => {
      storage.setItem(key, value);
      return Promise.resolve();
    },
    removeItem: (key) => {
      storage.removeItem(key);
      return Promise.resolve();
    },
  };
  return createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      storage: adapter,
      persistSession: true,
    },
  });
}
